import React, { useState } from 'react';
import {
  CheckCircle as StrengthIcon,
  ErrorOutlined as MissingIcon,
  ExpandLess as CollapseIcon,
  ExpandMore as ExpandIcon,
  LocalHospital as CptIcon,
  Warning as ConcernIcon,
} from '@mui/icons-material';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Collapse,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';

import type { JotCoachDocumentationAnalysis } from '../store/hooks';

import { ConfidenceMeter } from './ConfidenceMeter';

interface BillingSupportCardProps {
  billingSupport: JotCoachDocumentationAnalysis['billing_support'];
}

export const BillingSupportCard: React.FC<BillingSupportCardProps> = ({ billingSupport }) => {
  const [showStrengths, setShowStrengths] = useState(false);
  const [showConcerns, setShowConcerns] = useState(true);
  const [showMissing, setShowMissing] = useState(true);

  return (
    <Card
      sx={{
        borderRadius: 2,
        border: '1px solid',
        borderColor: billingSupport.supports_suggested_cpt ? 'success.light' : 'warning.light',
        bgcolor: 'background.paper',
      }}
    >
      <CardContent>
        {/* Header with CPT Code */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <CptIcon sx={{ color: 'primary.main', fontSize: 28 }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              Suggested CPT Code
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              {billingSupport.proposed_primary_code}
            </Typography>
          </Box>
          <Box>
            <Chip
              label={billingSupport.supports_suggested_cpt ? 'Supported' : 'Needs Work'}
              color={billingSupport.supports_suggested_cpt ? 'success' : 'warning'}
              size="small"
              sx={{ mb: 0.5 }}
            />
            <ConfidenceMeter confidence={billingSupport.confidence} showLabel size="small" />
          </Box>
        </Box>

        <Stack spacing={1.5}>
          {/* Strengths */}
          {billingSupport.strengths.length > 0 && (
            <Box>
              <Box
                sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                onClick={() => setShowStrengths(!showStrengths)}
              >
                <StrengthIcon sx={{ color: 'success.main', fontSize: 18, mr: 1 }} />
                <Typography variant="subtitle2" sx={{ flex: 1, fontWeight: 600 }}>
                  Strengths ({billingSupport.strengths.length})
                </Typography>
                <IconButton size="small">
                  {showStrengths ? (
                    <CollapseIcon fontSize="small" />
                  ) : (
                    <ExpandIcon fontSize="small" />
                  )}
                </IconButton>
              </Box>
              <Collapse in={showStrengths}>
                <List dense sx={{ pl: 2 }}>
                  {billingSupport.strengths.map((strength, idx) => (
                    <ListItem key={idx} sx={{ py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 28 }}>
                        <StrengthIcon sx={{ fontSize: 16, color: 'success.main' }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={strength}
                       slotProps={{ primary: { variant: 'caption' } }}/>
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            </Box>
          )}

          {/* Concerns */}
          {billingSupport.concerns.length > 0 && (
            <Box>
              <Box
                sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                onClick={() => setShowConcerns(!showConcerns)}
              >
                <ConcernIcon sx={{ color: 'warning.main', fontSize: 18, mr: 1 }} />
                <Typography variant="subtitle2" sx={{ flex: 1, fontWeight: 600 }}>
                  Concerns ({billingSupport.concerns.length})
                </Typography>
                <IconButton size="small">
                  {showConcerns ? (
                    <CollapseIcon fontSize="small" />
                  ) : (
                    <ExpandIcon fontSize="small" />
                  )}
                </IconButton>
              </Box>
              <Collapse in={showConcerns}>
                <List dense sx={{ pl: 2 }}>
                  {billingSupport.concerns.map((concern, idx) => (
                    <ListItem key={idx} sx={{ py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 28 }}>
                        <ConcernIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={concern}
                       slotProps={{ primary: { variant: 'caption' } }}/>
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            </Box>
          )}

          {/* Missing Elements */}
          {billingSupport.missing_elements_for_code.length > 0 && (
            <Box>
              <Box
                sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                onClick={() => setShowMissing(!showMissing)}
              >
                <MissingIcon sx={{ color: 'error.main', fontSize: 18, mr: 1 }} />
                <Typography variant="subtitle2" sx={{ flex: 1, fontWeight: 600 }}>
                  Missing Elements ({billingSupport.missing_elements_for_code.length})
                </Typography>
                <IconButton size="small">
                  {showMissing ? (
                    <CollapseIcon fontSize="small" />
                  ) : (
                    <ExpandIcon fontSize="small" />
                  )}
                </IconButton>
              </Box>
              <Collapse in={showMissing}>
                <List dense sx={{ pl: 2 }}>
                  {billingSupport.missing_elements_for_code.map((element, idx) => (
                    <ListItem key={idx} sx={{ py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 28 }}>
                        <MissingIcon sx={{ fontSize: 16, color: 'error.main' }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={element}
                       slotProps={{ primary: { variant: 'caption' } }}/>
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            </Box>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};
